import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useEffect, useState } from "react";
import { Flame, Trophy, LogOut, Menu, X, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import ThemeToggle from "../ThemeToggle";
import Logo from "../Logo";
import SearchAutosuggest from "../SearchAutosuggest";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinkCls = ({ isActive }) =>
    `relative text-sm font-medium transition-colors hover:text-foreground ${
      isActive ? "text-foreground" : "text-muted-foreground"
    }`;

  return (
    <header
      className={`sticky top-0 z-50 w-full ${scrolled ? "glass-bar" : "border-b border-transparent bg-background"}`}
      data-testid="site-header"
    >
      {/* Coupon strip */}
      <div className="coupon-strip text-center text-xs sm:text-sm py-2 px-4" data-testid="coupon-strip">
        <Sparkles className="inline h-3 w-3 mr-1" />
        Launch sale: <span className="font-bold">50% off</span> all practice tests with code{" "}
        <span className="rounded bg-white/20 px-2 py-0.5 font-mono font-bold">LAUNCH50</span>{" "}
        — limited time
      </div>

      <div className="container-x flex h-20 items-center gap-4">
        <Logo />

        <nav className="hidden lg:flex items-center gap-6 ml-2">
          <NavLink to="/marketplace" className={navLinkCls} data-testid="nav-marketplace">Marketplace</NavLink>
          <NavLink to="/pricing" className={navLinkCls} data-testid="nav-pricing">Pricing</NavLink>
          <NavLink to="/leaderboard" className={navLinkCls} data-testid="nav-leaderboard">Leaderboard</NavLink>
          {user && (
            <NavLink to="/dashboard" className={navLinkCls} data-testid="nav-dashboard">Dashboard</NavLink>
          )}
        </nav>

        <SearchAutosuggest className="hidden xl:flex items-center mx-3" />

        <div className="ml-auto flex items-center gap-3 shrink-0">
          <ThemeToggle className="hidden sm:inline-flex" />
          {user ? (
            <>
              <div className="hidden sm:flex items-center gap-2 text-sm">
                <span className="flex items-center gap-1 rounded-full bg-orange-500/10 px-2.5 py-1 text-[color:var(--brand-flame)]">
                  <Flame className="h-3.5 w-3.5 flame-anim" />
                  <b>{user.streak || 0}</b>
                </span>
                <span className="flex items-center gap-1 rounded-full bg-yellow-400/10 px-2.5 py-1 text-yellow-600 dark:text-yellow-400">
                  <Trophy className="h-3.5 w-3.5" />
                  <b>{user.xp || 0}</b> XP
                </span>
              </div>
              {user.role === "admin" && (
                <Link to="/admin" className="hidden sm:inline-flex">
                  <Button variant="outline" size="sm" data-testid="nav-admin">Admin</Button>
                </Link>
              )}
              <button
                onClick={async () => { await logout(); navigate("/"); }}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Logout</span>
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="hidden sm:inline-block text-sm font-medium text-muted-foreground hover:text-foreground" data-testid="nav-login">
                Sign in
              </Link>
              <Link to="/register" className="hidden sm:inline-block">
                <Button className="h-10 btn-primary" data-testid="nav-signup">
                  Get started free
                </Button>
              </Link>
            </>
          )}

          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-background" data-testid="mobile-menu">
          <div className="container-x py-4 flex flex-col gap-3">
            <Link to="/marketplace" className="py-2" onClick={() => setOpen(false)}>Marketplace</Link>
            <Link to="/pricing" className="py-2" onClick={() => setOpen(false)}>Pricing</Link>
            <Link to="/leaderboard" className="py-2" onClick={() => setOpen(false)}>Leaderboard</Link>
            {user && <Link to="/dashboard" className="py-2" onClick={() => setOpen(false)}>Dashboard</Link>}
            {user?.role === "admin" && <Link to="/admin" className="py-2" onClick={() => setOpen(false)}>Admin</Link>}
            {!user && (
              <div className="flex flex-col gap-2 pt-2 border-t border-border mt-2">
                <Link to="/login" className="py-2" onClick={() => setOpen(false)}>Sign in</Link>
                <Link to="/register" className="py-2 font-bold text-[color:var(--brand-flame)]" onClick={() => setOpen(false)}>Get started free</Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
